import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, HumiLab, DialogueBox } from 'components/UIElements'
import { replaceInString } from 'utils/misc'
import { linkt, forgetHumi } from '../../../assets/assets'
import '../stylesheets/login.scss'

import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'
import packageJson from '../../../../package.json'

const LoginPage = ({
  clearLoginError,
  clearTempPassword,
  forgotPassword,
  forgotUsername,
  isPositiveMessage,
  landingMessage,
  login,
  loginError,
  openNotice,
  password,
  strings,
  updatePassword,
  updateUsername,
  username,
  humiIsabellaDisabled
}) => {
  const safeUsername = username ?? ''
  const safePassword = password ?? ''
  const [content, setContent] = useState('login')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  useEffect(() => {
    window.history.pushState(null, null, window.location.href)
    window.onpopstate = () => {
      const onLoginPage = !!document.querySelector('#login-page_button_log-in')
      if (onLoginPage) {
        window.history.go(-1)
      } else {
        window.history.go(1)
        setContent('login')
      }
    }
    clearTempPassword()
    logSentryEvent({
      message: SENTRY_MESSAGES.loginScreenViewed,
      tags: { flow: 'auth', action: 'login_screen_viewed' },
    })

    return () => {
      window.onpopstate = null
    }
  }, [])

  const onSubmit = e => {
    if (e) e.preventDefault()
    logSentryEvent({
      message: SENTRY_MESSAGES.uiSignIn,
      tags: { flow: 'auth', action: 'ui_sign_in' },
    })
    login(username, password).then(() => {
      updatePassword('')
    })
  }

  const onForgotPW = e => {
    if (e) e.preventDefault()
    clearLoginError()
    forgotPassword(username)
    setContent('tempPWSent')
  }
  const onForgotUsername = e => {
    if (e) e.preventDefault()
    clearLoginError()
    forgotUsername(username)
    setContent('usernameSent')
  }

  const onBackToSignin = e => {
    if (e) e.preventDefault()
    clearLoginError()
    updateUsername('')
    setContent('login')
  }

  const onClickNotice = e => {
    e.preventDefault()
    openNotice({
      className: 'notice',
      noButtons: true,
      closeOnBackgroundClick: true,
      humi: !humiIsabellaDisabled,
      happyHumi: isPositiveMessage,
      closeX: true
    })
  }

  const loginForm = () => {
    return (
      <form onSubmit={onSubmit}>
        <div className='logo-title'>
          <img src={linkt} className='logo' alt='linkt-logo' />
        </div>
        {landingMessage && (
          <Button
            className='important-notice'
            onClick={onClickNotice}
            content='Important Notice'
            no-margin
            noStyling
          />
        )}
        <p className='error-msg'>{loginError}</p>
        <div className='field'>
          <label id='login-page_input_username'>
            {strings.username}
            <input
              autoFocus
              id='login-page_input_username'
              value={safeUsername}
              onChange={e => updateUsername(e.target.value)}
            />
          </label>
        </div>
        <div className='field password-field'>
          <label htmlFor='login-page_input_password'>{strings.password}</label>
          <div className='password-input-wrapper'>
            <input
              id='login-page_input_password'
              type={isPasswordVisible ? 'text' : 'password'}
              onChange={e => updatePassword(e.target.value)}
              value={safePassword}
            />
            <button
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              aria-pressed={isPasswordVisible}
              className='password-visibility-toggle'
              onClick={() => setIsPasswordVisible(visible => !visible)}
              type='button'
            >
              <i
                aria-hidden='true'
                className={`fas ${isPasswordVisible ? 'fa-eye-slash' : 'fa-eye'}`}
              />
            </button>
          </div>
        </div>
        <Button
          primary
          className='no-margin'
          content={strings.sign_in_btn}
          id='login-page_button_log-in'
          type='submit'
        />
        <Button
          className='forgot-btn'
          content={strings.forgot_username_password}
          link
          no-padding
          onClick={() => setContent('forgot')}
          type='button'
        />
        <p id='version' className='version'>
          {packageJson.version}
        </p>
      </form>
    )
  }

  const forgotForm = type => {
    const forgotPW = type === 'password'
    return (
      <form onSubmit={forgotPW ? onForgotPW : onForgotUsername}>
        <h2>{forgotPW ? strings.forgot_password : strings.forgot_username}</h2>
        <p className='sub-header'>
          {forgotPW ? strings.enter_username : strings.enter_email}
        </p>
        <div className='field'>
          <label id='login-page_input_username'>
            {forgotPW ? strings.username : strings.email_label}
            <input
              id='login-page_input_username'
              onChange={e => updateUsername(e.target.value)}
              type={forgotPW ? 'text' : 'email'}
              value={safeUsername}
            />
          </label>
        </div>
        <Button
          disabled={safeUsername === ''}
          className='no-margin'
          content={
            forgotPW ? strings.send_temp_password : strings.send_username
          }
          secondary
          type='submit'
        />
        <Button
          className='back-to-signin-btn'
          content={strings.back_signin}
          icon='fa fa-arrow-left'
          link
          onClick={onBackToSignin}
          type='button'
        />
      </form>
    )
  }

  const infoSentForm = type => {
    const forgotPW = type === 'password'
    return (
      <form>
        <h2>
          {forgotPW
            ? replaceInString(strings.temp_password_sent, '[Name]', safeUsername)
            : replaceInString(strings.username_sent, '[Name]', safeUsername)}
        </h2>
        <p className='sub-header'>
          {forgotPW
            ? strings.check_email_password
            : strings.check_email_username}
        </p>
        <Button
          className='back-to-signin-btn'
          content={strings.back_signin}
          icon='fa fa-arrow-left'
          link
          onClick={onBackToSignin}
          type='button'
        />
      </form>
    )
  }

  const forgotContent = () => {
    return (
      <form>
        <img className='forget-humi' src={forgetHumi} alt='forget-humi' />
        <h2 className='trouble'>{strings.trouble_signin}</h2>
        <p className='sub-header' />
        <Button
          className='forgot-btn'
          content={strings.sign_in_forgot_my_password}
          link
          onClick={() => setContent('forgotPW')}
          type='button'
        />
        <Button
          className='forgot-btn'
          content={strings.sign_in_forgot_my_username}
          link
          onClick={() => setContent('forgotUsername')}
          type='button'
        />
        <Button
          className='back-to-signin-btn'
          content={strings.back_signin}
          icon='fa fa-arrow-left'
          link
          onClick={onBackToSignin}
          type='button'
        />
      </form>
    )
  }

  return (
    <div className='login page'>
      <HumiLab />
      <DialogueBox className='login-container'>
        {content === 'login' && loginForm()}
        {content === 'forgot' && forgotContent()}
        {content === 'forgotPW' && forgotForm('password')}
        {content === 'forgotUsername' && forgotForm()}
        {content === 'tempPWSent' && infoSentForm('password')}
        {content === 'usernameSent' && infoSentForm()}
      </DialogueBox>
    </div>
  )
}

LoginPage.propTypes = {
  clearLoginError: PropTypes.func,
  clearTempPassword: PropTypes.func,
  forgotPassword: PropTypes.func,
  forgotUsername: PropTypes.func,
  isPositiveMessage: PropTypes.bool,
  landingMessage: PropTypes.bool,
  login: PropTypes.func,
  loginError: PropTypes.string,
  onAppOpened: PropTypes.func,
  openNotice: PropTypes.func,
  password: PropTypes.string,
  strings: PropTypes.objectOf(PropTypes.string),
  updatePassword: PropTypes.func,
  updateUsername: PropTypes.func,
  username: PropTypes.string
}

export default LoginPage
